/**
 * Tenant Comms Templates Tab - List View
 * 
 * Lists templates with actions. Create/Edit now navigate to full-page editor.
 * 
 * SAFETY:
 * - No schema changes
 * - Uses existing API endpoints
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Eye,
  PencilSimple,
  Copy,
  Archive,
  ArrowClockwise,
  CheckCircle,
  XCircle,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Auth fetch helper
const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
};

// Template type badges
const TYPE_CONFIG = {
  campaign: { color: "bg-purple-600/20 text-purple-400 border-purple-600/30", label: "Campaign" },
  automation: { color: "bg-blue-600/20 text-blue-400 border-blue-600/30", label: "Automation" },
  shared: { color: "bg-emerald-600/20 text-emerald-400 border-emerald-600/30", label: "Shared" },
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

// ============================================================================
// TEMPLATES TAB COMPONENT
// ============================================================================

export function TemplatesTab() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [typeFilter, setTypeFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  
  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/templates`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      } else {
        toast.error("Failed to load templates");
      }
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  // Filter templates
  const filteredTemplates = templates.filter((t) => {
    if (!showArchived && !t.is_active) return false;
    if (typeFilter !== "all" && t.template_type !== typeFilter) return false;
    return true;
  });

  // Navigate to new template editor
  const openNewEditor = () => {
    navigate("/admin/tenant-communications/templates/new?tab=templates");
  };

  // Navigate to edit template
  const openEditEditor = (template) => {
    navigate(`/admin/tenant-communications/templates/${template.template_id}?tab=templates`);
  };

  // Duplicate template
  const duplicateTemplate = async (template) => {
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/templates/${template.template_id}/duplicate`, {
        method: "POST",
      });

      if (res.ok) {
        toast.success("Template duplicated");
        fetchTemplates();
      } else {
        toast.error("Failed to duplicate template");
      }
    } catch (err) {
      console.error("Failed to duplicate:", err);
      toast.error("Failed to duplicate template");
    }
  };

  // Archive template
  const archiveTemplate = async (template) => {
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/templates/${template.template_id}`, {
        method: "DELETE",
      });

      if (res.ok) {
        toast.success("Template archived");
        fetchTemplates();
      } else {
        toast.error("Failed to archive template");
      }
    } catch (err) {
      console.error("Failed to archive:", err);
      toast.error("Failed to archive template");
    }
  };

  // Preview template
  const previewTemplate = async (template) => {
    setLoadingPreview(true);
    setPreviewOpen(true);
    setPreviewData(null);

    try {
      const res = await authFetch(`${API}/admin/tenant-comms/templates/${template.template_id}/preview`, {
        method: "POST",
        body: JSON.stringify({}),
      });

      if (res.ok) {
        const data = await res.json();
        setPreviewData(data);
      } else {
        toast.error("Failed to load preview");
        setPreviewOpen(false);
      }
    } catch (err) {
      console.error("Failed to preview:", err);
      toast.error("Failed to load preview");
      setPreviewOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-700">
        <CardContent className="py-12">
          <div className="text-center text-zinc-400">
            <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
            <span className="text-zinc-300">Loading templates...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Actions */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40 bg-zinc-800 border-zinc-600 text-zinc-100">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-600">
              <SelectItem value="all" className="text-zinc-100">All Types</SelectItem>
              <SelectItem value="campaign" className="text-zinc-100">Campaign</SelectItem>
              <SelectItem value="automation" className="text-zinc-100">Automation</SelectItem>
              <SelectItem value="shared" className="text-zinc-100">Shared</SelectItem>
            </SelectContent>
          </Select>
          
          <label className="flex items-center gap-2 text-zinc-400 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-zinc-600"
            />
            Show archived
          </label>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            onClick={fetchTemplates}
            variant="ghost"
            className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
          >
            <ArrowClockwise size={18} className="mr-2" />
            Refresh
          </Button>
          <Button
            onClick={openNewEditor}
            className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
          >
            <Plus size={18} className="mr-2" />
            New Template
          </Button>
        </div>
      </div>

      {/* Templates Table */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-zinc-100 flex items-center gap-2">
            <FileText size={20} className="text-[#FF6600]" />
            Email Templates
          </CardTitle>
          <CardDescription className="text-zinc-400">
            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? "s" : ""}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-8 text-zinc-400">
              <FileText size={40} className="mx-auto mb-3 opacity-50" />
              <p className="text-zinc-300">No templates found</p>
              <p className="text-sm text-zinc-500">Create your first template to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-700 hover:bg-transparent">
                    <TableHead className="text-zinc-300">Name</TableHead>
                    <TableHead className="text-zinc-300">Type</TableHead>
                    <TableHead className="text-zinc-300">Subject</TableHead>
                    <TableHead className="text-zinc-300">Status</TableHead>
                    <TableHead className="text-zinc-300">Updated</TableHead>
                    <TableHead className="text-zinc-300 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTemplates.map((template) => {
                    const typeConfig = TYPE_CONFIG[template.template_type] || TYPE_CONFIG.shared;
                    
                    return (
                      <TableRow
                        key={template.template_id}
                        className="border-zinc-700 hover:bg-zinc-800/50"
                      >
                        <TableCell className="text-zinc-100 font-medium">
                          <button
                            onClick={() => openEditEditor(template)}
                            className="hover:text-[#FF6600] transition-colors text-left"
                          >
                            {template.name}
                          </button>
                          {template.description && (
                            <p className="text-zinc-500 text-xs mt-0.5 truncate max-w-xs">
                              {template.description}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={typeConfig.color}>
                            {typeConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-zinc-400 max-w-xs truncate">
                          {template.subject}
                        </TableCell>
                        <TableCell>
                          {template.is_active ? (
                            <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-600/30">
                              <CheckCircle size={12} className="mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-zinc-700 text-zinc-400 border-zinc-600">
                              <XCircle size={12} className="mr-1" />
                              Archived
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-zinc-400 text-sm">
                          {formatDate(template.updated_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => previewTemplate(template)}
                              className="h-8 w-8 p-0 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700"
                              title="Preview"
                            >
                              <Eye size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditEditor(template)}
                              className="h-8 w-8 p-0 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700"
                              title="Edit"
                            >
                              <PencilSimple size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => duplicateTemplate(template)}
                              className="h-8 w-8 p-0 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700"
                              title="Duplicate"
                            >
                              <Copy size={16} />
                            </Button>
                            {template.is_active && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => archiveTemplate(template)}
                                className="h-8 w-8 p-0 text-zinc-400 hover:text-red-400 hover:bg-zinc-700"
                                title="Archive"
                              >
                                <Archive size={16} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============================================
          PREVIEW DIALOG (kept for quick previews)
          ============================================ */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-3xl w-[90vw] max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-8 pt-8 pb-6 border-b border-zinc-700 flex-shrink-0">
            <DialogTitle className="text-zinc-100 text-xl flex items-center gap-3">
              <Eye size={28} className="text-[#FF6600]" />
              Template Preview
            </DialogTitle>
            <DialogDescription className="text-zinc-400 mt-2">
              Preview with sample data. Actual values will be substituted when sent.
            </DialogDescription>
          </DialogHeader>
          
          {loadingPreview ? (
            <div className="flex-1 flex items-center justify-center py-16">
              <ArrowClockwise size={28} className="animate-spin text-zinc-400" />
            </div>
          ) : previewData ? (
            <div className="flex-1 overflow-y-auto px-8 py-8 space-y-6">
              {/* Subject Preview */}
              <div className="space-y-3">
                <p className="text-zinc-400 text-sm">Subject</p>
                <div className="bg-zinc-800 rounded-xl p-5 border border-zinc-700">
                  <p className="text-zinc-100 text-lg">{previewData.rendered_subject}</p>
                </div>
              </div>
              
              {/* Body Preview */}
              <div className="space-y-3">
                <p className="text-zinc-400 text-sm">Email Body</p>
                <div className="bg-white rounded-xl p-8 border border-zinc-300 max-h-[400px] overflow-y-auto">
                  <div
                    className="prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: previewData.rendered_body }}
                  />
                </div>
              </div>
            </div>
          ) : null}
          
          <DialogFooter className="px-8 py-6 border-t border-zinc-700 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-800 px-6"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TemplatesTab;
